import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { useDreams } from "../contexts/DreamsContext";
import {
  Home, Compass, Star, Crown, LogOut, ArrowLeft, LayoutGrid, Trophy, LifeBuoy, Sparkles, CalendarRange, Gift,
} from "lucide-react";

const navItems = [
  { to: "/dreams",           label: "Dashboard",      icon: Home,       end: true },
  { to: "/dreams/vision",    label: "Vida de Sonho",  icon: Compass },
  { to: "/dreams/sonhos",    label: "Sonhos",         icon: Star },
  { to: "/dreams/board",     label: "Board",          icon: LayoutGrid },
  { to: "/dreams/timeline",  label: "Timeline",       icon: CalendarRange },
  { to: "/dreams/top",       label: "Top Sonhadores", icon: Trophy },
  { to: "/dreams/suporte",   label: "Apoio",          icon: LifeBuoy },
  { to: "/dreams/subscribe", label: "Subscrição",     icon: Crown },
];

export default function DreamsLayout() {
  const { me, isSubscribed, emTrial, diasRestantes } = useDreams();
  const navigate = useNavigate();

  return (
    <div className="dreams-root min-h-screen" data-testid="dreams-root">
      <header className="sticky top-0 z-20 bg-white/85 backdrop-blur-xl border-b border-[#E6E1D6]">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center gap-4 md:gap-6">
          <Link
            to="/my"
            data-testid="dreams-back-my"
            className="flex items-center gap-1.5 dreams-eyebrow hover:text-[#0F1829] transition-colors"
            title="Voltar ao MYIHTP"
          >
            <ArrowLeft size={12} strokeWidth={2} /> MYIHTP
          </Link>

          <Link to="/dreams" className="flex items-center gap-2.5 group" data-testid="dreams-logo">
            <div className="w-9 h-9 rounded-full bg-[#0F1829] grid place-items-center text-white shrink-0 group-hover:scale-105 transition-transform">
              <Sparkles size={16} strokeWidth={2} className="text-[#FAF8F3]" />
            </div>
            <div className="hidden sm:block leading-tight">
              <div
                className="text-base text-[#0F1829]"
                style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}
              >
                IHTP Dreams
              </div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-[#978D78] mt-0.5">
                Acompanhamento Virtual
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-2 lg:ml-6">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`nav-${it.to.split("/").pop() || "dashboard"}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] transition-all ${
                    isActive
                      ? "bg-[#0F1829] text-white shadow-[2px_2px_12px_rgba(15,24,41,0.18)]"
                      : "text-[#4B5563] hover:bg-[#F5F2EB] hover:text-[#0F1829]"
                  }`
                }
                style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 500 }}
              >
                <it.icon size={13} strokeWidth={1.8} />
                {it.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2 md:gap-3">
            {isSubscribed && emTrial && (
              <Link
                to="/dreams/subscribe"
                data-testid="dreams-trial-badge"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-600/30 text-[10px] text-emerald-700 font-semibold uppercase tracking-wider hover:bg-emerald-500/20 transition-colors"
                title="Período gratuito de 3 meses"
              >
                <Gift size={10} strokeWidth={2} />
                {diasRestantes != null ? `Grátis · ${diasRestantes}d` : "Período grátis"}
              </Link>
            )}
            {isSubscribed && !emTrial && (
              <span
                data-testid="dreams-active-badge"
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#6F4F1D]/8 border border-[#6F4F1D]/25 text-[10px] text-[#6F4F1D] font-semibold uppercase tracking-wider"
              >
                <Crown size={10} fill="#6F4F1D" strokeWidth={0} /> Sonhador Activo
              </span>
            )}
            <button
              onClick={() => navigate("/")}
              data-testid="dreams-exit"
              className="text-[#4B5563] hover:text-[#B91C1C] hover:bg-red-50 px-2.5 py-1.5 rounded-full text-[11px] flex items-center gap-1 transition-colors"
              title="Sair de Dreams"
            >
              <LogOut size={13} strokeWidth={1.8} />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        <nav className="md:hidden flex items-center gap-1 px-4 pb-3 overflow-x-auto scrollbar-hide">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap transition ${
                  isActive
                    ? "bg-[#0F1829] text-white"
                    : "text-[#4B5563] bg-[#F5F2EB] hover:text-[#0F1829]"
                }`
              }
            >
              <it.icon size={12} strokeWidth={1.8} /> {it.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-10">
        {me && <Outlet />}
      </main>
    </div>
  );
}
